<template>
  <div>
    <div class="mb-8 flex justify-start max-w-3xl">
      <h1 class="font-bold text-3xl">
        <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('stock.list')">Stock</inertia-link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ form.reference }}
      </h1>
    </div>
    <trashed-message v-if="stockData.deleted_at" class="mb-6" @restore="restore">
      This Stock has been deleted.
    </trashed-message>
    <div class="bg-white rounded shadow overflow-hidden max-w-6xl">
      <form @submit.prevent="submit">
        <div class="px-8 pb-2 pt-8 flex flex-wrap">
          <text-input v-model="form.reference" :error="errors.reference" class="pr-6 pb-8 w-full lg:w-1/4" label="Reference"/>

          <text-input v-model="form.bin" :error="errors.bin" class="pr-6 pb-8 w-full lg:w-1/4" label="Bin"/>

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label class="form-label">Brand:</label>
            <multiselect v-model="form.brand" deselect-label="supprimer" track-by="name" label="name" placeholder="Select one" :options="brands"
                         :error="errors.brand" :searchable="true" open-direction="bottom" @input="asyncFind">
              <template slot="singleLabel" slot-scope="{ option }"><strong>{{ option.name }}</strong></template>
            </multiselect>
          </div>

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label class="form-label">Model:</label>
            <multiselect v-model="form.model" deselect-label="supprimer" track-by="name" label="name" placeholder="Select one"
                         :options="modelsData"
                         :error="errors.model" :searchable="true" open-direction="bottom" :clear-on-select="false" :loading="isLoading">
              <template slot="singleLabel" slot-scope="{ option }"><strong>{{ option.name }}</strong></template>
            </multiselect>
          </div>
        </div>

        <div class="px-8 py-2 flex flex-wrap">
          <text-input v-model="form.cost" :error="errors.cost" class="pr-6 pb-8 w-full lg:w-1/3" label="Cost"/>

          <select-input v-model="form.category" :error="errors.category" class="pr-6 pb-8 w-full lg:w-1/3" label="Category">
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select-input>

          <select-input v-model="form.type" :error="errors.type" class="pr-6 pb-8 w-full lg:w-1/3" label="Type">
            <option v-for="type in types" :key="type" :value="type">{{ type }}</option>
          </select-input>
        </div>

        <div class="px-8 py-2 pt-10 relative m-5 flex flex-wrap border-gray-200 border rounded-md">
          <div class="flex flex-col w-5/12">
            <h4 class="top-2 left-2 pb-4 font-bold text-lg">Quantité assigner par shop</h4>
            <div v-for="shop in $page.props.shops" class="flex flex-row pb-4">
              <label class="py-3 w-5/12">
                <input type="checkbox" class="form-checkbox disabled:opacity-50" disabled="disabled" :checked="form.shopsQuantity[shop.id] || form.shopsQuantity[shop.id] === 0" :value="shop.id" @change="toggle(shop.id)">
                <span class="mx-2 text-md font-bold text-gray-600">{{ shop.name }}</span>
              </label>
              <jet-input type="number" min="0" v-if="isActivated(shop.id)" disabled="disabled" class="w-24 disabled:opacity-50" v-model="form.shopsQuantity[shop.id]" placeholder="0"
                         :error="errors.shopsQuantity"/>
            </div>
          </div>
          <div class="flex flex-col w-2/12"></div>
          <div class="flex flex-col w-5/12">
            <h4 class="top-2 left-2 pb-4 font-bold text-lg">Ajout de stock</h4>
            <div v-for="shop in $page.props.shops" class="flex flex-row pb-4">
              <label class="py-3 w-5/12">
                <input type="checkbox" class="form-checkbox disabled:opacity-50" :checked="form.shopsAddQuantity[shop.id] || form.shopsAddQuantity[shop.id] === 0" :value="shop.id" @change="toggleAdd(shop.id)">
                <span class="mx-2 text-md font-bold text-gray-600">{{ shop.name }}</span>
              </label>
              <jet-input type="number" :min="1" :oninput="shop.type !== 1 ? `validity.valid||(value='')` : ''" v-if="isAddActivated(shop.id)" class="w-24" v-model="form.shopsAddQuantity[shop.id]" placeholder="0" :error="errors.shopsAddQuantity"/>
            </div>
          </div>
        </div>

        <div class="px-8 py-4 bg-gray-100 border-t border-gray-200 flex items-center">
          <button v-if="!stockData.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete Stock
          </button>
          <button class="text-yellow-500 ml-8 hover:underline" tabindex="-1" type="button" @click="showReturnForm = true">Return Stock
          </button>

          <inertia-link class="flex items-center group py-3 mx-4 text-indigo-600 group-hover:text-white" @click.prevent="exportStock" href="#">
            <div>Stock History</div>
          </inertia-link>

          <inertia-link class="flex items-center group py-3 mx-4 text-indigo-600 group-hover:text-white" @click.prevent="exportShopStock" href="#">
            <div >Shops Stock History</div>
          </inertia-link>

          <loading-button :loading="sending" class="btn-indigo ml-auto" type="submit">Update Stock</loading-button>
        </div>
      </form>
    </div>
    <!-- Detail Modal -->
    <jet-dialog-modal max-width="sm" :show="showReturnForm" :closeable="false" @close="showReturnForm = false">
      <template #title>
        Retourner piece Ref : {{ form.reference }}
      </template>
      <template #content>
        <div class="mt-4">
          <div class="flex flex-wrap">
            <select-input v-model="returnForm.shopId" :error="errors.shopId" class="px-3 pb-6 w-full" label="Shop">
              <option v-for="shop in $page.props.shops" :key="shop.id" :value="shop.id">{{ shop.name }}</option>
            </select-input>
            <div class="px-3 pb-6 w-full">
              <label class="form-label">Quantité:</label>
              <jet-input type="number" min="1" v-model="returnForm.quantity" :error="errors.quantity"/>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <jet-secondary-button @click.native="showReturnForm = false">
          Annuler
        </jet-secondary-button>
        <jet-button class="mr-10" :class="{ 'opacity-25': processing }" :disabled="processing" @click.native="returnStock">
          Retourner
        </jet-button>
      </template>
    </jet-dialog-modal>
  </div>
</template>

<script>
import Layout from '@/Layouts/AppLayout'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import JetInput from '@/Jetstream/Input'
import TrashedMessage from '@/Shared/TrashedMessage'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetSecondaryButton from '@/Jetstream/SecondaryButton'
import JetButton from '@/Jetstream/Button'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  metaInfo() {
    return {
      title: `${this.form.reference}`,
    }
  },
  layout: Layout,
  components: {
    LoadingButton,
    SelectInput,
    TextInput,
    JetInput,
    TrashedMessage,
    JetDialogModal,
    JetButton,
    JetSecondaryButton,
    Multiselect
  },
  props: {
    errors: Object,
    stockData: Object,
    models: Array,
    brands: Array,
    categories: Array,
    shopsStock: Object,
    //assignedStock: Object,
  },
  remember: 'form',
  data() {
    return {
      types: ['part', 'phone', 'accessory'],
      modelsData: this.models,
      isLoading: false,
      sending: false,
      processing: false,
      showReturnForm: false,
      returnForm: {
        stockId: null,
        shopId: null,
        quantity: null,
      },
      form: {
        shopsQuantity: null,
        shopsAddQuantity: null,
        activeShops: [],
        activeAddShops: [],
        id: this.stockData.id,
        reference: this.stockData.reference,
        bin: this.stockData.bin,
        type: this.stockData.type,
        cost: this.stockData.cost,
        model: this.stockData.model,
        brand: this.stockData.brand,
        category: this.stockData.category.id,
      },
    }
  },
  methods: {
    exportStock() {
      window.open(this.route('stock.history', this.stockData.id), "_blank");
    },
    exportShopStock() {
      window.open(this.route('stock.shopHistory', this.stockData.id), "_blank");
    },
    submit() {
      var data = new FormData()
      data.append('reference', this.form.reference || '')
      data.append('bin', this.form.bin || '')
      data.append('type', this.form.type || '')
      data.append('cost', this.form.cost || '')
      data.append('model', this.form.model.id || '')
      data.append('brand', this.form.brand.id || '')
      data.append('category', this.form.category || '')
      let shopsQuantity = JSON.stringify(this.form.shopsAddQuantity)
      data.append('shopsQuantity', shopsQuantity)
      data.append('activeShops', this.form.activeAddShops)
      data.append('_method', 'put')

      this.$inertia.post(this.route('stock.update', this.stockData.id), data, {
        onStart: () => this.sending = true,
        onFinish: () => this.sending = false,
        onSuccess: () => {
          if (Object.keys(this.$page.props.errors).length === 0) {
            /*this.$inertia.replace(this.route('stock.edit', this.stockData.id))*/
          }
        },
      })
    },
    toggle(id) {
      let hiddenCard = this.form.activeShops.find(idx => idx === id)
      if (hiddenCard) {
        this.form.activeShops = this.form.activeShops.filter((x) => x !== id)
      } else {
        this.form.activeShops = this.form.activeShops.concat(id)
      }
    },
    toggleAdd(id) {
      let hiddenCard = this.form.activeAddShops.find(idx => idx === id)
      if (hiddenCard) {
        this.form.activeAddShops = this.form.activeAddShops.filter((x) => x !== id)
      } else {
        this.form.activeAddShops = this.form.activeAddShops.concat(id)
      }
    },
    isActivated(id) {
      return this.form.activeShops.findIndex(idx => idx === id) > -1;
    },
    isAddActivated(id) {
      return this.form.activeAddShops.findIndex(idx => idx === id) > -1;
    },
    asyncFind() {
      const params = {
        brand: null,
      };
      this.isLoading = true
      if (this.form.brand != null) {
        params.brand = this.form.brand.id
      }

      return axios.get(this.route('stock.searchModel'), {params})
        .then(response => {
          this.model = null
          this.modelsData = response.data[0]
          this.isLoading = false
        })
    },
    returnStock() {
      if (confirm("Confirmer l'action de retour ?")) {
        var data = new FormData()
        data.append('id', this.stockData.id || '')
        data.append('shopId', this.returnForm.shopId || '')
        data.append('quantity', this.returnForm.quantity || '')

        this.$inertia.post(this.route('defect.returnDefectFromStock'), data, {
          onStart: () => this.sending = true,
          onFinish: () => {
            this.sending = false
            this.showReturnForm = false
            this.$inertia.get(this.route('stock.edit', this.stockData.id))
          },
          onSuccess: () => {
            this.showReturnForm = false
            if (Object.keys(this.$page.props.errors).length === 0) {

            }
          },
          onError: (errors) => {
            console.log(errors)
            alert(JSON.stringify(errors))
          },
        })
      }
    },
    destroy() {
      if (confirm('Are you sure you want to delete this Stock?')) {
        this.$inertia.delete(this.route('stock.destroy', this.stockData.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this stock?')) {
        this.$inertia.put(this.route('stock.restore', this.stockData.id))
      }
    },
  },
  created() {
    const shopsQuantity = {}
    this.$page.props.shops.forEach((shop) => {
      shopsQuantity[shop.id] = null
      if (this.shopsStock[shop.id] !== null && this.shopsStock[shop.id] !== undefined) {
        shopsQuantity[shop.id] = this.shopsStock[shop.id].quantity
      }
    })
    this.form.shopsQuantity = shopsQuantity

    for (const [key, value] of Object.entries(this.shopsStock)) {
      this.form.activeShops = this.form.activeShops.concat(Number(key))
    }

    const shopsAddQuantity = {}
    this.$page.props.shops.forEach((shop) => {
      shopsAddQuantity[shop.id] = null
    })
    this.form.shopsAddQuantity = shopsAddQuantity

/*    for (const [key, as] of Object.entries(this.assignedStock)) {
      this.form.activeAddShops = this.form.activeAddShops.concat(Number(as.shop_id))
      this.form.shopsAddQuantity[as.shop_id] = Number(as.quantity)
    }*/
  }
}
</script>
